import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Power, Loader2 } from 'lucide-react';
import { useTaxonomyList, SECTOR_TAXONOMY_QK } from './useSectorTaxonomy';
import {
  createTaxonomy,
  patchTaxonomy,
  type SectorTaxonomyNode,
} from '../../api/sectorTaxonomyApi';
import { InfoTooltip } from '../../components/InfoTooltip';
import { TX_TOOLTIPS } from './tooltipTexts';

interface Props {
  pushToast: (msg: string, tone?: 'info' | 'success' | 'error' | 'warn') => void;
}

interface CreateFormState {
  canonicalSectorId: string;
  displayName: string;
  parentSectorId: string;
  description: string;
}

const EMPTY_FORM: CreateFormState = {
  canonicalSectorId: '',
  displayName: '',
  parentSectorId: '',
  description: '',
};

// 탭 A — 정규 섹터 트리 (1레벨 flat, parent 는 들여쓰기로 표시).
export function TaxonomyTreeTab({ pushToast }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const query = useTaxonomyList({
    isActive: includeInactive ? undefined : true,
    search: search || undefined,
  });
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ── 딥링크 하이라이트 (from FeedbackTab targetRef): ?highlight=sec_xxx
  // 로드된 목록에 해당 id 가 있으면 scroll + 배경 펄스. 없으면 토스트 경고 + 전체 목록 유지.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  // 쿼리 로드 결과에 대해 한 번만 warn — 토스트 중복 방지.
  const warnedRef = useRef<string | null>(null);

  // useLayoutEffect 로 DOM commit 직후 동기 처리 + 미존재 시 재시도.
  // 이전 구현은 rAF 1회 만에 ref 를 찾아 scroll 시도 → children 렌더가 한 프레임 뒤에
  // 이뤄지는 경우 ref 가 비어 있어 scroll/activeHighlight 가 조용히 누락되는 버그가 있었음.
  useLayoutEffect(() => {
    if (!highlightId) {
      setActiveHighlight(null);
      warnedRef.current = null;
      return;
    }
    // 데이터 로드 전에는 아직 판단 불가.
    if (!query.data) return;
    const found = query.data.some((n) => n.canonicalSectorId === highlightId);
    if (!found) {
      if (warnedRef.current !== highlightId) {
        warnedRef.current = highlightId;
        pushToast(
          `해당 정규 섹터(${highlightId})를 찾지 못했습니다. 비활성 토글 후 재확인하세요.`,
          'warn'
        );
      }
      setActiveHighlight(null);
      // 쿼리 파라미터는 유지하되, 전체 목록은 그대로 보여준다.
      return;
    }
    setActiveHighlight(highlightId);

    // DOM 커밋 후 ref 가 붙을 때까지 최대 2회 재시도:
    // 1차: rAF (다음 paint — 형제 노드 렌더 완료 기대)
    // 2차: setTimeout 100ms (QueryClient 상태 전이 + 자식 노드 지연 렌더 대비)
    let rafId: number | null = null;
    let timeoutId: number | null = null;
    let cancelled = false;

    const tryScroll = (attempt: number): void => {
      if (cancelled) return;
      const el = rowRefs.current.get(highlightId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (attempt === 0) {
        rafId = requestAnimationFrame(() => tryScroll(1));
      } else if (attempt === 1) {
        timeoutId = window.setTimeout(() => tryScroll(2), 100);
      } else {
        // 최종 실패 — DOM 에 해당 행이 렌더되지 못한 상태.
        // 데이터에는 있는데 DOM 에 없는 건 거의 발생 안 하므로 debug 로그만.
        // eslint-disable-next-line no-console
        console.debug(
          '[TaxonomyTreeTab] highlight row not found in DOM after retries',
          highlightId
        );
      }
    };

    rafId = requestAnimationFrame(() => tryScroll(0));

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [highlightId, query.data, pushToast]);

  // 하이라이트 해제 — 행 클릭 시 URL 쿼리 제거 (애니메이션 재실행 방지).
  const clearHighlight = () => {
    if (!highlightId) return;
    const sp = new URLSearchParams(searchParams);
    sp.delete('highlight');
    setSearchParams(sp, { replace: true });
    setActiveHighlight(null);
  };

  // 1레벨 flat — parent 있는 노드는 하위로 분류.
  // 운영 안전장치: parent_sector_id 가 있지만 해당 parent 가 현재 로드된 목록에
  // 존재하지 않는 고아(orphan) child 는 roots 로 편입한다. 그렇지 않으면 고아 노드는
  // DOM 에 렌더되지 않아 딥링크 하이라이트도 적용되지 않는다.
  const grouped = useMemo(() => {
    const items = query.data ?? [];
    const ids = new Set(items.map((n) => n.canonicalSectorId));
    const roots: SectorTaxonomyNode[] = [];
    const byParent = new Map<string, SectorTaxonomyNode[]>();
    items.forEach((n) => {
      if (!n.parentSectorId) {
        roots.push(n);
        return;
      }
      if (!ids.has(n.parentSectorId)) {
        // 고아 — parent 가 현재 목록에 없음 (비활성 필터 등). roots 로 편입.
        // eslint-disable-next-line no-console
        console.warn(
          '[TaxonomyTreeTab] orphan child promoted to root',
          n.canonicalSectorId,
          'parent=',
          n.parentSectorId
        );
        roots.push(n);
        return;
      }
      const arr = byParent.get(n.parentSectorId) ?? [];
      arr.push(n);
      byParent.set(n.parentSectorId, arr);
    });
    return { roots, byParent };
  }, [query.data]);

  async function handleCreate(): Promise<void> {
    if (submitting) return;
    if (!form.canonicalSectorId.trim() || !form.displayName.trim()) {
      pushToast('slug / 표시명은 필수입니다.', 'warn');
      return;
    }
    setSubmitting(true);
    try {
      await createTaxonomy({
        canonicalSectorId: form.canonicalSectorId.trim(),
        displayName: form.displayName.trim(),
        parentSectorId: form.parentSectorId.trim() || null,
        description: form.description.trim() || null,
      });
      pushToast('정규 섹터 생성됨.', 'success');
      setForm(EMPTY_FORM);
      setCreating(false);
      qc.invalidateQueries({ queryKey: [SECTOR_TAXONOMY_QK.taxonomy] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'create_failed';
      pushToast(`생성 실패: ${msg}`, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive(n: SectorTaxonomyNode): Promise<void> {
    if (togglingId) return;
    setTogglingId(n.canonicalSectorId);
    try {
      await patchTaxonomy(n.canonicalSectorId, { isActive: !n.isActive });
      pushToast(
        n.isActive
          ? `${n.displayName} 비활성화 완료`
          : `${n.displayName} 활성화 완료`,
        'success'
      );
      qc.invalidateQueries({ queryKey: [SECTOR_TAXONOMY_QK.taxonomy] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'toggle_failed';
      pushToast(`상태 변경 실패: ${msg}`, 'error');
    } finally {
      setTogglingId(null);
    }
  }

  function renderRow(n: SectorTaxonomyNode, isChild: boolean): JSX.Element {
    const isHighlight = activeHighlight === n.canonicalSectorId;
    return (
      <div
        key={n.canonicalSectorId}
        ref={(el) => {
          if (el) rowRefs.current.set(n.canonicalSectorId, el);
          else rowRefs.current.delete(n.canonicalSectorId);
        }}
        data-testid={`tx-tree-row-${n.canonicalSectorId}`}
        onClick={isHighlight ? clearHighlight : undefined}
        className={
          'tx-tree__row' +
          (isChild ? ' tx-tree__row--child' : '') +
          (!n.isActive ? ' tx-tree__row--inactive' : '') +
          (isHighlight ? ' tx-tree__row--highlight' : '')
        }
      >
        <div className="tx-tree__name">
          <strong>{n.displayName}</strong>
          <span className="tx-tree__slug">{n.canonicalSectorId}</span>
          {!n.isActive && <span className="tx-status tx-status--rejected">비활성</span>}
          {(n.pendingMappingCount ?? 0) > 0 && n.symbolCount === 0 && (
            <span
              className="tx-tree__warn"
              role="note"
              aria-label={`승인 대기 매핑 ${n.pendingMappingCount}건 — 매핑 탭에서 승인하면 종목 수가 집계됩니다.`}
              tabIndex={0}
              data-tooltip={
                `승인 대기 매핑 ${n.pendingMappingCount}건입니다. 운영자가 매핑 탭에서 승인하면 ` +
                `stock_canonical_sectors VIEW 에 반영되어 종목 수가 집계됩니다.`
              }
            >
              ⚠ 대기 {n.pendingMappingCount}
            </span>
          )}
        </div>
        <span className="tx-tree__count">{n.symbolCount.toLocaleString('ko-KR')} 종목</span>
        <span className="tx-tree__count">
          {(n.confirmedMappingCount ?? n.mappingCount).toLocaleString('ko-KR')} 확정
          {(n.pendingMappingCount ?? 0) > 0 && (
            <>
              {' · '}
              <span className="tx-tree__count--pending">
                {n.pendingMappingCount!.toLocaleString('ko-KR')} 대기
              </span>
            </>
          )}
        </span>
        <button
          type="button"
          className="pca-btn"
          onClick={() => handleToggleActive(n)}
          disabled={togglingId === n.canonicalSectorId}
          aria-label={n.isActive ? '비활성화' : '활성화'}
        >
          {togglingId === n.canonicalSectorId ? (
            <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
          ) : (
            <Power size={14} strokeWidth={1.75} />
          )}
          {n.isActive ? '비활성화' : '활성화'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="pca-filter">
        <div className="pca-filter__group" style={{ flex: 1, minWidth: 260 }}>
          <label className="pca-filter__label" htmlFor="tx-search">
            검색
          </label>
          <input
            id="tx-search"
            className="pca-input"
            type="text"
            placeholder="canonical_sector_id 또는 표시명"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">비활성 포함</label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            include inactive
          </label>
        </div>
        <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
          <label className="pca-filter__label">&nbsp;</label>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={() => setCreating((v) => !v)}
          >
            <Plus size={14} strokeWidth={1.75} />
            새 정규 섹터
          </button>
        </div>
      </div>

      {creating && (
        <div className="pca-filter" style={{ marginTop: 8 }}>
          <div className="pca-filter__group">
            <label className="pca-filter__label">slug</label>
            <input
              className="pca-input"
              placeholder="sec_semiconductor"
              value={form.canonicalSectorId}
              onChange={(e) => setForm({ ...form, canonicalSectorId: e.target.value })}
            />
          </div>
          <div className="pca-filter__group">
            <label className="pca-filter__label">표시명</label>
            <input
              className="pca-input"
              placeholder="반도체"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
          </div>
          <div className="pca-filter__group">
            <label className="pca-filter__label">parent (선택)</label>
            <input
              className="pca-input"
              placeholder=""
              value={form.parentSectorId}
              onChange={(e) => setForm({ ...form, parentSectorId: e.target.value })}
            />
          </div>
          <div className="pca-filter__group" style={{ flex: 1, minWidth: 200 }}>
            <label className="pca-filter__label">설명</label>
            <input
              className="pca-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
            <label className="pca-filter__label">&nbsp;</label>
            <button
              type="button"
              className="pca-btn pca-btn--primary"
              onClick={handleCreate}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
              ) : (
                <Plus size={14} strokeWidth={1.75} />
              )}
              생성
            </button>
          </div>
        </div>
      )}

      {query.isError && (
        <div className="pca-warning-banner">
          트리 로드 실패: {(query.error as Error).message}
        </div>
      )}

      <div className="tx-tree" style={{ marginTop: 12 }}>
        {/* 컬럼 헤더 — div grid 레이아웃과 맞춘 고정 row.
            확정·대기 컬럼에 ⓘ 부착해 VIEW 집계 규칙을 운영자에게 안내.
            "섹터명" 으로 표기해 요약 배너의 "정규 섹터" 카운트와 혼동 방지. */}
        <div className="tx-tree__row tx-tree__row--header" role="row">
          <span className="tx-tree__name tx-tree__header-cell">섹터명</span>
          <span className="tx-tree__count tx-tree__header-cell">종목</span>
          <span className="tx-tree__count tx-tree__header-cell">
            확정 · 대기
            <InfoTooltip
              label={TX_TOOLTIPS.confirmedPending}
              testId="tx-tip-confirmed-pending"
              placement="left"
            />
          </span>
          <span className="tx-tree__header-cell">&nbsp;</span>
        </div>
        {query.isLoading && (
          <div className="tx-tree__row">
            <span className="pca-muted">로딩 중…</span>
          </div>
        )}
        {!query.isLoading && grouped.roots.length === 0 && (
          <div className="tx-tree__row">
            <span className="pca-muted">등록된 정규 섹터가 없습니다.</span>
          </div>
        )}
        {grouped.roots.map((root) => {
          const children = grouped.byParent.get(root.canonicalSectorId) ?? [];
          return (
            <div key={root.canonicalSectorId}>
              {renderRow(root, false)}
              {children.map((c) => renderRow(c, true))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
